import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { GOOGLE_FONTS, onboardingBrandKitSchema, type OnboardingBrandKitInput } from '@/lib/schemas/brandKit';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/authStore';
import { OnboardingShell } from '@/components/layout/OnboardingShell';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { ColorField } from '@/components/forms/ColorField';
import { FileUploader } from '@/components/forms/FileUploader';
import { toast } from '@/components/ui/Toast';
import { queryKeys } from '@/lib/api/queryKeys';
import { BRAND_DEFAULTS } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';

export function BrandKitPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const patchUser = useAuthStore((s) => s.patchUser);
  const [uploading, setUploading] = useState(false);
  const t = useT();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<OnboardingBrandKitInput>({
    resolver: zodResolver(onboardingBrandKitSchema),
    defaultValues: {
      campaignName: '',
      primaryColor: BRAND_DEFAULTS.primaryColor,
      secondaryColor: BRAND_DEFAULTS.secondaryColor,
      font: BRAND_DEFAULTS.font,
      clientLogo: '',
    },
  });

  const primary = watch('primaryColor');
  const secondary = watch('secondaryColor');
  const logo = watch('clientLogo');

  const save = useMutation({
    mutationFn: (input: OnboardingBrandKitInput) =>
      api.post('/auth/brandkit', { ...input, gameMechanic: 'wheel' }),
    onSuccess: () => {
      patchUser({ hasBrandKit: true });
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.brandKit });
      toast({
        tone: 'success',
        title: t('onboarding.kit.savedTitle'),
        description: t('onboarding.kit.savedBody'),
      });
      navigate('/', { replace: true });
    },
    onError: () => toast({ tone: 'error', title: t('onboarding.kit.errorTitle') }),
  });

  return (
    <OnboardingShell
      step={3}
      title={t('onboarding.kit.title')}
      subtitle={t('onboarding.kit.subtitle')}
    >
      <form className="grid gap-5" onSubmit={handleSubmit((v) => save.mutate(v))}>
        <FormField
          label={t('onboarding.kit.campaignName')}
          required
          error={errors.campaignName?.message}
        >
          <Input
            placeholder={t('onboarding.kit.campaignNamePlaceholder')}
            {...register('campaignName')}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <ColorField
            label={t('onboarding.kit.primary')}
            value={primary}
            onChange={(v) => setValue('primaryColor', v, { shouldValidate: true })}
          />
          <ColorField
            label={t('onboarding.kit.secondary')}
            value={secondary}
            onChange={(v) => setValue('secondaryColor', v, { shouldValidate: true })}
          />
        </div>

        <FormField label={t('onboarding.kit.font')} required error={errors.font?.message}>
          <Select {...register('font')}>
            {GOOGLE_FONTS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField
          label={t('onboarding.kit.clientLogo')}
          required
          error={errors.clientLogo?.message}
        >
          <FileUploader
            value={logo || null}
            onChange={(v) => setValue('clientLogo', v ?? '', { shouldValidate: true })}
            onUploadingChange={setUploading}
            preset="logo"
          />
        </FormField>

        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            size="lg"
            loading={save.isPending}
            disabled={uploading}
          >
            {t('onboarding.kit.finish')}
          </Button>
        </div>
      </form>
    </OnboardingShell>
  );
}
